import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Download, FileText, Eye, Clock, Printer, Share2, Shield,
  ChevronRight, Search,
} from 'lucide-react';
import { patientApi } from '../api';
import type { PatientCertificateDoc, HealthVaultItem } from '../types';
import NHCIdBadge from '../components/NHCIdBadge';
import Spinner from '../../../components/ui/Spinner';

function fmtDate(d?: string | null) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

const STATUS_BADGE: Record<string, string> = {
  ISSUED: 'bg-emerald-50 text-emerald-700',
  VERIFIED: 'bg-teal-50 text-teal-700',
  REVOKED: 'bg-red-50 text-red-600',
  EXPIRED: 'bg-gray-100 text-gray-500',
  DRAFT: 'bg-blue-50 text-blue-600',
};

type Tab = 'certificates' | 'documents';

export default function DocumentDownloads() {
  const navigate = useNavigate();
  const [certs, setCerts] = useState<PatientCertificateDoc[]>([]);
  const [docs, setDocs] = useState<HealthVaultItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [nhcId, setNhcId] = useState('');
  const [tab, setTab] = useState<Tab>('certificates');
  const [shares, setShares] = useState(0);
  const [search, setSearch] = useState('');

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all([
      patientApi.getCertificates(),
      patientApi.getHealthVault(),
      patientApi.getProfile(),
      // Sharing a document IS a consent grant, so the real count is here.
      // The tile below used to sum `certificate.shares`, a field this API does
      // not send — so "Active Shares" read 0 however many were live.
      patientApi.getConsents().catch(() => []),
    ]).then(([c, d, p, g]) => {
      if (alive) { setCerts(c); setDocs(d); setNhcId(p.nhcId); setShares(g.filter((x) => x.status === 'ACTIVE').length); }
    }).catch(() => {}).finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const filteredCerts = certs.filter(c => {
    if (!search) return true;
    const s = search.toLowerCase();
    return c.title?.toLowerCase().includes(s) || c.certificateType?.toLowerCase().includes(s);
  });

  const filteredDocs = docs.filter(d => {
    if (!search) return true;
    const s = search.toLowerCase();
    return d.title?.toLowerCase().includes(s) || d.category?.toLowerCase().includes(s);
  });

  if (loading) return <div className="flex justify-center py-20"><Spinner size="lg" /></div>;

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Download className="w-6 h-6 text-teal-600" /> Downloads
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">Preview, print, and download your NeoHealthCard documents</p>
        </div>
        {nhcId && <NHCIdBadge nhcId={nhcId} size="sm" />}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="card p-4 text-center">
          <Shield className="w-5 h-5 text-teal-500 mx-auto mb-1" />
          <p className="text-2xl font-bold text-gray-900">{certs.length}</p>
          <p className="text-[10px] text-gray-500">Certificates</p>
        </div>
        <div className="card p-4 text-center">
          <FileText className="w-5 h-5 text-blue-500 mx-auto mb-1" />
          <p className="text-2xl font-bold text-gray-900">{docs.length}</p>
          <p className="text-[10px] text-gray-500">Documents</p>
        </div>
        <div className="card p-4 text-center">
          <Shield className="w-5 h-5 text-emerald-500 mx-auto mb-1" />
          <p className="text-2xl font-bold text-gray-900">{certs.filter(c => c.verificationStatus === 'VERIFIED').length}</p>
          <p className="text-[10px] text-gray-500">NeoVerified</p>
        </div>
        <div className="card p-4 text-center">
          <Share2 className="w-5 h-5 text-purple-500 mx-auto mb-1" />
          <p className="text-2xl font-bold text-gray-900">{shares}</p>
          <p className="text-[10px] text-gray-500">Active Shares</p>
        </div>
      </div>

      {/* Tabs + Search */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex gap-2">
          <button onClick={() => setTab('certificates')}
            className={`px-4 py-2 text-xs font-medium rounded-full ${tab === 'certificates' ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
            Certificates ({certs.length})
          </button>
          <button onClick={() => setTab('documents')}
            className={`px-4 py-2 text-xs font-medium rounded-full ${tab === 'documents' ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
            Documents ({docs.length})
          </button>
        </div>
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search..." className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/30" />
        </div>
      </div>

      {/* Certificate List */}
      {tab === 'certificates' && (
        <div className="space-y-2">
          {filteredCerts.length === 0 ? (
            <div className="card p-8 text-center">
              <Shield className="w-10 h-10 text-gray-200 mx-auto mb-2" />
              <p className="text-gray-400 text-sm">No certificates found</p>
              <button onClick={() => navigate('/patient/documents/templates')} className="text-teal-600 text-xs hover:underline mt-2">Generate a certificate</button>
            </div>
          ) : filteredCerts.map(cert => (
            <div key={cert.id} className="card p-4 flex items-center gap-4 hover:shadow-sm transition-shadow cursor-pointer"
              onClick={() => navigate(`/patient/certificates/preview/${cert.id}`)}>
              <div className="w-10 h-10 rounded-lg bg-teal-50 flex items-center justify-center shrink-0">
                <Shield className="w-5 h-5 text-teal-600" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-semibold text-gray-900 truncate">{cert.title}</p>
                  <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded ${STATUS_BADGE[cert.status] || ''}`}>{cert.status}</span>
                </div>
                <div className="flex items-center gap-3 text-[10px] text-gray-500 mt-0.5">
                  <span>{cert.certificateType.replace(/_/g, ' ')}</span>
                  <span>·</span>
                  <span className="flex items-center gap-0.5"><Clock className="w-3 h-3" /> {fmtDate(cert.issueDate)}</span>
                  {cert.neoVerifyId && <span className="text-teal-600 flex items-center gap-0.5"><Shield className="w-3 h-3" /> NeoVerified</span>}
                </div>
              </div>
              <div className="flex gap-1 shrink-0">
                <button className="p-2 text-gray-400 hover:text-teal-600 rounded-lg hover:bg-teal-50" title="Preview">
                  <Eye className="w-4 h-4" />
                </button>
                <button onClick={e => { e.stopPropagation(); window.print(); }}
                  className="p-2 text-gray-400 hover:text-teal-600 rounded-lg hover:bg-teal-50" title="Download">
                  <Download className="w-4 h-4" />
                </button>
                <ChevronRight className="w-4 h-4 text-gray-300 mt-2" />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Document List */}
      {tab === 'documents' && (
        <div className="space-y-2">
          {filteredDocs.length === 0 ? (
            <div className="card p-8 text-center">
              <FileText className="w-10 h-10 text-gray-200 mx-auto mb-2" />
              <p className="text-gray-400 text-sm">No documents found</p>
            </div>
          ) : filteredDocs.map(doc => (
            <div key={doc.id} className="card p-4 flex items-center gap-4 hover:shadow-sm transition-shadow cursor-pointer"
              onClick={() => navigate(`/patient/documents/preview/${doc.id}`)}>
              <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
                <FileText className="w-5 h-5 text-blue-600" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-semibold text-gray-900 truncate">{doc.title}</p>
                  {doc.verificationStatus === 'VERIFIED' && (
                    <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded bg-teal-50 text-teal-700">VERIFIED</span>
                  )}
                </div>
                <div className="flex items-center gap-3 text-[10px] text-gray-500 mt-0.5">
                  <span>{doc.category.replace(/_/g, ' ')}</span>
                  <span>·</span>
                  <span>{doc.issuedBy || 'NeoHealthCard'}</span>
                  <span>·</span>
                  <span className="flex items-center gap-0.5"><Clock className="w-3 h-3" /> {fmtDate(doc.issuedAt)}</span>
                </div>
              </div>
              <div className="flex gap-1 shrink-0">
                <button className="p-2 text-gray-400 hover:text-blue-600 rounded-lg hover:bg-blue-50" title="Preview">
                  <Eye className="w-4 h-4" />
                </button>
                <ChevronRight className="w-4 h-4 text-gray-300 mt-2" />
              </div>
            </div>
          ))}
        </div>
      )}

      <p className="text-[10px] text-gray-400 text-center">
        PDF download uses browser print-to-PDF. Backend PDF generation (pdfkit) available as future enhancement. All downloads are audit-logged.
      </p>
    </div>
  );
}
